import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import { HpiDataTable, HpiDateFilterBar, HpiPagination } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface VendorRef {
  id: number
  name: string
}

interface ReferralCase {
  id: number
  file_number: number | null
  case_name: string | null
  date_added: string | null
  investigation_type: string | null
  other_type: string | null
  referred_by: string | null
  referred_by_contact_id: number | null
  referred_by_customer_id: number | null
  vendor_referral: VendorRef | null
  how_found: string | null
  case_total: number
  status: string
}

interface Props {
  customer: { id: number; display_name: string }
  cases: ReferralCase[]
  start_date: string
  end_date: string
}

/* ── Helpers ────────────────────────────────────────────── */

function formatCurrency(amount: number | null): string {
  if (amount === null || amount === undefined) return "-"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

/* ── Columns ───────────────────────────────────────────── */

const PER_PAGE = 25

const caseColumns: HpiColumn<ReferralCase>[] = [
  {
    header: "ID",
    width: "max-w-[60px]",
    align: "right",
    render: (row) => (
      <Link
        href={`/cases/${row.id}`}
        className="text-sm text-blue-600 hover:underline"
      >
        {row.file_number ?? row.id}
      </Link>
    ),
  },
  {
    header: "Date",
    width: "max-w-[100px]",
    render: (row) => (
      <div className="text-sm">{formatDate(row.date_added)}</div>
    ),
  },
  {
    header: "Type",
    render: (row) => (
      <div className="text-sm">
        {row.investigation_type}
        {row.other_type && (
          <div className="text-muted-foreground text-xs">{row.other_type}</div>
        )}
      </div>
    ),
  },
  {
    header: "Case",
    render: (row) => (
      <Link
        href={`/cases/${row.id}`}
        className="text-sm text-blue-600 hover:underline"
      >
        {row.case_name}
      </Link>
    ),
  },
  {
    header: "Referred By",
    render: (row) => (
      <div className="text-sm">
        {row.referred_by && row.referred_by_customer_id ? (
          <Link
            href={`/customers/${row.referred_by_customer_id}/reports/referrals`}
            className="text-blue-600 hover:underline"
          >
            {row.referred_by}
          </Link>
        ) : (
          (row.referred_by ?? null)
        )}
        {row.vendor_referral && (
          <Link
            href={`/vendors/${row.vendor_referral.id}`}
            className="text-blue-600 hover:underline"
          >
            {row.vendor_referral.name}
          </Link>
        )}
        {!row.referred_by && !row.vendor_referral && "-"}
      </div>
    ),
  },
  {
    header: "How Found",
    render: (row) => <div className="text-sm">{row.how_found ?? ""}</div>,
  },
  {
    header: "Est. Billable",
    width: "max-w-[120px]",
    align: "right",
    render: (row) => (
      <div className="text-sm">
        {row.case_total > 0 ? formatCurrency(row.case_total) : ""}
      </div>
    ),
  },
  {
    header: "Status",
    width: "max-w-[120px]",
    align: "right",
    render: (row) => <div className="text-sm">{row.status}</div>,
  },
]

/* ── Main Component ────────────────────────────────────── */

export default function CustomerReferrals({
  customer,
  cases,
  start_date,
  end_date,
}: Props) {
  const [startDate, setStartDate] = useState(start_date)
  const [endDate, setEndDate] = useState(end_date)
  const [page, setPage] = useState(1)

  const totalPages = Math.ceil(cases.length / PER_PAGE)
  const paginated = cases.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Referrals", href: "#" },
  ]

  const handleUpdate = () => {
    setPage(1)
    router.get(
      `/customers/${customer.id}/reports/referrals`,
      { start_date: startDate, end_date: endDate },
      { preserveState: true },
    )
  }

  const caseCount = cases.length
  const caseTotal = cases.reduce((sum, c) => sum + c.case_total, 0)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Referrals - ${customer.display_name}`} />

      <div className="p-6">
        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        />

        <HpiDataTable<ReferralCase>
          title={`Referred Cases — ${customer.display_name}`}
          columns={caseColumns}
          data={paginated}
          keyFn={(row) => row.id}
          emptyMessage="No referred cases found for the selected date range."
          pagination={
            cases.length > PER_PAGE ? (
              <HpiPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={cases.length}
                perPage={PER_PAGE}
                onPageChange={setPage}
              />
            ) : undefined
          }
        />

        {cases.length > 0 && (
          <div className="mt-2 flex justify-end gap-8 text-sm font-semibold">
            <span>{caseCount} Cases</span>
            <span>{formatCurrency(caseTotal)}</span>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
